/**
 * testCatalogHelpers.ts
 * Pure helpers shared by the Test Catalogue master-detail page (PR B rebuild).
 * No JSX, no MUI — just formatting + GUID utilities so the view files stay
 * lean (CODING-STANDARDS 600-line cap). List filter/sort/status logic lives
 * in testDefinitionModel.ts.
 */

/** Unit options for the test catalogue (mirrors the legacy grid column). */
export const TEST_UNIT_OPTIONS = ['Each', 'Stage', 'Hour', 'Set'] as const;

/** {value: MachineKey, label: "Code — Name"} pair for the picker. */
export interface MachineOption {
  value: string;
  label: string;
}

/**
 * Case-insensitive GUID equality. SQL Server returns uniqueidentifier
 * UPPER-cased while client GUIDs are lower-cased, so ALL GUID comparisons in
 * this feature must be case-insensitive.
 */
export function guidEquals(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a == null || b == null) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Format a CAD amount for display. The test catalogue stores prices in CAD
 * (the base currency), so this is a straight Intl currency format — no FX.
 */
export function formatCad(amount: number | null | undefined): string {
  const n = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  return n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

/**
 * Capable machine options for a given test, derived from the capable-machine
 * map (lower-cased test key → lower-cased machine keys). Absent key = empty set.
 * Returns the {value,label} options for ONLY the capable, currently-active
 * machines (resolved against the full active-machine option list).
 */
export function capableOptionsForTest(
  testKey: string | null | undefined,
  capableMap: Record<string, string[]>,
  allMachineOptions: readonly MachineOption[],
): MachineOption[] {
  if (!testKey) return [];
  const capableKeys = capableMap[testKey.toLowerCase()] ?? [];
  if (capableKeys.length === 0) return [];
  return allMachineOptions.filter((o) =>
    capableKeys.some((ck) => guidEquals(ck, o.value)),
  );
}
